import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Flipped, Flipper, HandleEnterUpdateDelete } from 'react-flip-toolkit';
import { apiModelTypeToAutomationStepType } from '../../../../extViewmodels/sdk/Utils';
import { useAutomationEditorContext } from '../../../containers/EditAutomationTemplate/hooks';
import { EditAutomationStep } from '../../../containers/EditAutomationTemplate/presentation';
import { EditAutomationTemplateItemSeparator } from '../EditAutomationTemplateItemSeparator';
import { styleSheet } from './styles';

export const EditAutomationTemplateSteps = React.forwardRef<
	Flipper,
	{
		children?: React.ReactChild;
		onStepSaved?: () => void;
		automationTemplate: Api.IAutomationTemplate;
		impersonationContext: Api.IImpersonationContext;
	}
>(function EditAutomationTemplateStepsBase({ children, onStepSaved, automationTemplate, impersonationContext }, ref) {
	// Use the custom hook for managing steps
	const { steps } = useAutomationEditorContext();

	const onHandleEnterUpdateDelete = React.useCallback<HandleEnterUpdateDelete>(
		({ hideEnteringElements, animateEnteringElements, animateExitingElements, animateFlippedElements }) => {
			// simulatneous animations
			hideEnteringElements();
			animateExitingElements();
			animateFlippedElements();
			animateEnteringElements();
		},
		[]
	);

	const onAnimateIn = React.useCallback((el: HTMLElement, index: number) => {
		requestAnimationFrame(() => {
			el.style.transition = `opacity 0ms`;
			el.style.opacity = `0`;
			requestAnimationFrame(() => {
				el.style.transition = `opacity ${250 + index * 100}ms ease`;
				el.style.opacity = `1`;
			});
		});
	}, []);

	const onAnimateOut = React.useCallback((el: HTMLElement, index: number, removeElement: () => void) => {
		requestAnimationFrame(() => {
			const time = 250 + index * 100;
			el.style.transition = `opacity ${time}ms ease`;
			el.style.opacity = `0`;
			setTimeout(removeElement, time);
		});
	}, []);

	// Memoize flipKey to avoid recalculation on every render
	const flipKey = React.useMemo(() => steps?.map(x => x.id).join('') || '', [steps]);

	if (!automationTemplate) {
		return null;
	}

	return (
		<>
			<Flipper flipKey={flipKey} handleEnterUpdateDelete={onHandleEnterUpdateDelete} ref={ref}>
				{steps?.map((x, i) => {
					// Convert API _type (e.g., "SwitchAutomationStep") to enum format (e.g., "Switch")
					const stepType = x?._type ? apiModelTypeToAutomationStepType(x._type as Api.ApiModelType) : undefined;
					const isSwitchStep = stepType === Api.AutomationStepType.Switch;
					const isSwitchStepChild = !isSwitchStep && !!automationTemplate?.parentTemplateId;
					const addPadding = i !== 0 && i === steps.length - 1 && !x;

					// Unsaved templates have no id yet but we need to differentiate them
					const key = x.id || `${automationTemplate?.parentTemplateId}-unsaved`;
					return (
						<Flipped flipId={x.id} key={key} onAppear={onAnimateIn} onExit={onAnimateOut}>
							<div className={addPadding ? css(styleSheet.lastStep) : null}>
								{isSwitchStepChild ? (
									<div className={css(styleSheet.stepSeparator)} />
								) : (
									<EditAutomationTemplateItemSeparator className={css(styleSheet.stepSeparator)} />
								)}
								<EditAutomationStep
									automationTemplateId={automationTemplate.id}
									impersonationContext={impersonationContext}
									onStepChanged={onStepSaved}
									index={i}
									key={x.id}
									step={x}
								/>
							</div>
						</Flipped>
					);
				})}
			</Flipper>
			{children}
		</>
	);
});
